import { useState, useEffect, useMemo } from "react";
import { useDisclosure } from "@chakra-ui/react";

import MetaHead from "components/base/MetaHead";
import Button from "components/base/Button";

import MainContainer from "components/sections/MainContainer";
import FloatingCTA from "components/blocks/FloatingCTA";
import HeroHeader from "components/sections/HeroHeader";
import ModalVideo from "components/sections/ModalVideo";
import EpisodesSection from "components/sections/EpisodesSection";

import { allowedLocales } from "translations";

import Trans from "components/base/Trans";

import { useRouter } from "next/router";

import {
  getAllEpisodes,
  getEpisodesPage,
  getEpisodeRaw,
  transformEpisode,
} from "lib/payloadApi";
import { useLivePreview } from "@payloadcms/live-preview-react";
import PreviewBanner from "components/base/PreviewBanner";

function buildStructuredData(episode) {
  const title =
    episode.data.meta_title ||
    (episode.data.title[0] && episode.data.title[0].text
      ? episode.data.title[0].text
      : "");
  const description =
    episode.data.meta_description || episode.data.description || "";
  const uploadDate =
    episode.data.published_date || episode.first_publication_date || "";

  return JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "VideoObject",
        name: title,
        description: description,
        thumbnailUrl: episode.data.thumbnail ? episode.data.thumbnail.url : "",
        embedUrl: `https://www.youtube.com/embed/${episode.data.feature_video}`,
        uploadDate: uploadDate,
        publisher: {
          "@type": "Organization",
          name: "Hill Helicopters",
          url: "https://www.hillhelicopters.com",
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://www.hillhelicopters.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Docuseries",
            item: "https://www.hillhelicopters.com/docuseries",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: title,
          },
        ],
      },
    ],
  });
}

export default function Docuseries({
  initialEpisodes,
  episode,
  rawEpisode,
  preview,
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const router = useRouter();
  const [videoId, setVideoId] = useState(null);
  const [{ allEpisodes, page, loading }, setPageState] = useState({
    allEpisodes: initialEpisodes,
    page: 1,
    loading: false,
  });

  const { data: liveRawData } = useLivePreview({
    serverURL: process.env.NEXT_PUBLIC_PAYLOAD_URL || "http://localhost:3080",
    depth: 2,
    initialData: rawEpisode || {},
  });

  const episodeData = useMemo(() => {
    if (preview && liveRawData && liveRawData.id) {
      try {
        return transformEpisode(liveRawData);
      } catch {
        return episode;
      }
    }
    return episode;
  }, [preview, liveRawData, episode]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVideoId(episodeData.data.feature_video);
    onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episodeData]);

  const loadMore = () => {
    setPageState({ allEpisodes, page: page + 1, loading: true });
  };

  useEffect(() => {
    const selectedLocale =
      allowedLocales.indexOf(router.locale) > -1 ? router.locale : null;

    getEpisodesPage(1, selectedLocale).then((newPage) => {
      setPageState({ allEpisodes: newPage, page: 1, loading: false });
    });
  }, [router.locale]);

  useEffect(() => {
    if (loading) {
      getEpisodesPage(page, router.locale).then((newPage) => {
        const newEpisodesObject = Object.assign(allEpisodes, {
          results: allEpisodes.results.concat(newPage.results),
          page,
        });

        setPageState({ allEpisodes: newEpisodesObject, page, loading: false });
      });
    }
  }, [page]);

  return (
    <MainContainer
      navStyle={{ gradient: true, inverted: false }}
      bg="black.900"
    >
      <MetaHead
        title={episodeData.data.meta_title || episodeData.data.title[0].text}
        description={
          episodeData.data.meta_description || episodeData.data.description
        }
        imageUrl={episodeData.data.thumbnail?.url}
        structuredData={buildStructuredData(episodeData)}
      />
      <HeroHeader
        height="80vh"
        imageSrc={{
          base: "/images/docuseries-header-mobile.jpg",
          md: "/images/docuseries-header.jpg",
        }}
        title={<Trans id="LBL_DOCUSERIES_TITLE" />}
        titleType="headline_md"
        subtitle={<Trans id="LBL_DOCUSERIES_SUBTITLE" />}
        description={<Trans id="LBL_DOCUSERIES_DESCRIPTION" />}
        descriptionType="p_md"
        noScrollHint
        cta={
          <Button
            onClick={() => {
              document
                .getElementById("presentation")
                .scrollIntoView({ behavior: "smooth" });
            }}
            trackLabel="docuseries_watch_episodes"
            mt={[4, null, 12]}
            mr={[2, 4, 6]}
          >
            <Trans id="LBL_DOCUSERIES_CTA" />
          </Button>
        }
      />
      <EpisodesSection
        allEpisodes={allEpisodes}
        loading={loading}
        loadMore={loadMore}
        onEpisodeClick={(id, uid) => {
          setVideoId(id);
          onOpen();
          router.push(`/docuseries?id=${uid}`, undefined, {
            shallow: true,
          });
        }}
      />
      <ModalVideo
        isOpen={isOpen}
        onClose={() => {
          router.push(`/docuseries`, undefined, { shallow: true });
          onClose();
        }}
        videoId={videoId}
      />
      <FloatingCTA />
      {preview && <PreviewBanner />}
    </MainContainer>
  );
}

export async function getStaticProps({ params, locale, preview }) {
  const selectedLocale = allowedLocales.indexOf(locale) > -1 ? locale : null;

  const allEpisodes = await getAllEpisodes(selectedLocale);
  const episode = allEpisodes.results.find((e) => e.uid === params.uid);

  if (!episode) {
    return {
      notFound: true,
      revalidate: 60,
    };
  }

  let rawEpisode = null;
  if (preview) {
    try {
      rawEpisode = await getEpisodeRaw(params.uid, selectedLocale, true);
    } catch {
      // fall back to null if raw fetch fails
    }
  }

  return {
    props: {
      initialEpisodes: allEpisodes,
      episode,
      rawEpisode,
      preview: preview || false,
    },
    revalidate: 60,
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}
